import { Chip } from '@mui/material';

import { getStatusColor } from '@/pages/Administration/Devices/Devices.service';
import type {
  LimaSourceAggregatedStatusModel,
  StatusGroup
} from '@/pages/Administration/Devices/SyncDevices/SyncDevices.types';

import useStyles from '../DevicesTable.styles';
import type { DeviceSourceStatusChipsProps } from './DeviceSourceStatusChips.types';

/**
 * Builds chip configuration dynamically from status groups
 * @param statusGroups - Array of status group definitions
 * @returns Array of chip configurations
 */
const buildChipsConfig = (statusGroups: StatusGroup[]) =>
  statusGroups.map((group) => {
    let computeValue:
      | ((aggRow: LimaSourceAggregatedStatusModel) => number)
      | undefined;
    let key: string;

    // Special handling for combined 1xxx and null statuses
    if (group.codePrefix === '1/null') {
      key = 'group_1xxx+null';
      computeValue = (aggRow: LimaSourceAggregatedStatusModel) =>
        Number(aggRow.group_1xxx ?? 0) + Number(aggRow.group_null ?? 0);
    } else {
      key = `group_${group.codePrefix}xxx`;
    }

    return {
      key,
      color: group.color,
      label: group.label,
      computeValue
    };
  });

/**
 * Displays status counts grouped by status code as colored chips
 *
 * Renders Material-UI Chip components showing the count of sources
 * in each status category. Only displays chips with non-zero values.
 * Color is determined by the status group configuration.
 *
 * @param props - Component props
 * @returns JSX element with status count chips
 *
 * @example
 * ```tsx
 * <DeviceSourceStatusChips row={aggStatusData} />
 * ```
 */
const DeviceSourceStatusChips = (props: DeviceSourceStatusChipsProps) => {
  const { row } = props;
  const { classes } = useStyles();

  const chipsConfig = buildChipsConfig(row.statusGroups || []);

  return (
    <>
      {chipsConfig.map((chip) => {
        let value = 0;

        // Compute value using custom function or direct field access
        if (chip.computeValue) {
          value = chip.computeValue(row);
        } else if (row && chip.key in row) {
          value = Number((row as Record<string, unknown>)[chip.key]) || 0;
        }

        // Only render chips with positive values
        if (value > 0) {
          return (
            <Chip
              key={chip.key}
              className={classes.chipMarginGap}
              label={`${value}`}
              variant="filled"
              color={getStatusColor(chip.color)}
              size="small"
            />
          );
        }
        return null;
      })}
    </>
  );
};

export default DeviceSourceStatusChips;
